import { computed } from "@preact/signals";
import { useProductSelector } from "$store/sdk/useProductSelector.tsx";
import { useCart } from "apps/vtex/hooks/useCart.ts";
import { sendEvent } from "$store/sdk/analytics.tsx";
import { toAnalytics } from "$store/sdk/ga4/transform/toAnalytics.ts";
import type { ToAddToCart } from "$store/sdk/ga4/events/add_to_cart.ts";

let minicartDrawer: HTMLInputElement | null;

interface Props {
  analytics?: ToAddToCart;
}

export default function AddToCartButton(
  { analytics }: Props,
) {
  const { selectedProducts } = useProductSelector();
  const { addItems, loading } = useCart();

  const items = computed(() =>
    selectedProducts.value
      .filter((product) => product.isSelected)
      .map((product) => ({
        id: product.sku,
        quantity: product.quantity ?? 1,
        seller: product.seller ?? "1",
      }))
  );

  analytics = analytics && {
    items: analytics.items.filter((item) =>
      selectedProducts.value.find((product) =>
        product.sku === item.sku && product.isSelected
      )
    ),
    view: {
      id: analytics.view.id ?? "product_shelf_with_media",
      name: analytics.view.name ?? "Prateleira com mídias",
    },
  };

  const handleAddToCart = async (e: MouseEvent) => {
    const button = e.currentTarget as HTMLButtonElement;

    if (
      items.value.length &&
      !selectedProducts.value.some((product) => product.isSelected)
    ) {
      button.setAttribute("data-error", "true");
      return;
    }

    button.removeAttribute("data-error");

    try {
      loading.value = true;

      await addItems({ orderItems: items.value });

      if (analytics) {
        const add_to_cart = toAnalytics({
          type: "add_to_cart",
          data: analytics,
        });

        sendEvent(add_to_cart);
      }

      if (!minicartDrawer) {
        minicartDrawer = document.querySelector("#minicart-drawer");
      }

      if (minicartDrawer) minicartDrawer.checked = true;
    } finally {
      loading.value = false;
    }
  };

  return (
    <div class="mx-5 relative lg:mx-0 lg:flex-1">
      <button
        class="w-full h-12 rounded-lg bg-tw-secondary-200 text-white text-xl font-bold flex justify-center items-center hover:bg-tw-secondary-300 peer"
        disabled={loading.value || !items.value.length}
        onClick={handleAddToCart}
      >
        Adicionar ao carrinho
      </button>

      {!items.value.length && (
        <span class="mt-1 text-tw-danger-100 text-sm text-center font-bold hidden lg:mt-0.5 lg:whitespace-nowrap lg:absolute lg:top-full lg:right-0 peer-data-[error=true]:block xl:left-0">
          Selecione os produtos que deseja adicionar
        </span>
      )}
    </div>
  );
}
